import { useEffect, useState, useCallback } from "react";
import api from "../lib/api";
import { PageHeader, Card, fmtDate, StatusBadge, Th, Td, DateRangeFilter, rangeParams, IpTag, IP_TYPE_OPTIONS, LeadTag, hasPendingLead } from "../components/shared";
import { useAuth } from "../context/AuthContext";
import { Input } from "../components/ui/input";
import { Button } from "../components/ui/button";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../components/ui/select";
import { Popover, PopoverContent, PopoverTrigger } from "../components/ui/popover";
import { Checkbox } from "../components/ui/checkbox";
import { JourneyDialog } from "./Customers";
import { Smartphone, Monitor, Tablet, ChevronLeft, ChevronRight, Download, ChevronDown } from "lucide-react";
import { toast } from "sonner";

const DeviceIcon = ({ d }) => {
  const I = d === "Mobile" ? Smartphone : d === "Tablet" ? Tablet : Monitor;
  return <I className="h-3.5 w-3.5 text-slate-400 inline mr-1" />;
};

const OfferMultiSelect = ({ offers, value, onChange }) => {
  const toggle = (id) => onChange(value.includes(id) ? value.filter((v) => v !== id) : [...value, id]);
  const label = value.length === 0 ? "All offers" : value.length === 1 ? offers.find((o) => o.id === value[0])?.name || "1 offer" : `${value.length} offers selected`;
  return (
    <Popover>
      <PopoverTrigger asChild>
        <Button data-testid="clicks-offer-filter" variant="outline" className="w-60 justify-between bg-white font-normal">
          <span className="truncate">{label}</span><ChevronDown className="h-4 w-4 opacity-50" />
        </Button>
      </PopoverTrigger>
      <PopoverContent className="w-72 p-2" align="start">
        <div className="flex justify-between px-1 pb-2 text-xs">
          <span className="text-slate-500">Select one or more offers</span>
          <button data-testid="clicks-offer-clear" className="text-indigo-600 font-semibold" onClick={() => onChange([])}>Clear</button>
        </div>
        <div className="max-h-72 overflow-y-auto space-y-0.5">
          {offers.map((o) => (
            <label key={o.id} data-testid={`clicks-offer-option-${o.id}`} className="flex items-center gap-2 px-2 py-1.5 rounded hover:bg-slate-50 cursor-pointer text-sm">
              <Checkbox checked={value.includes(o.id)} onCheckedChange={() => toggle(o.id)} />
              <span className="truncate">{o.name}</span>
            </label>
          ))}
        </div>
      </PopoverContent>
    </Popover>
  );
};

const IpCheckBanner = () => {
  const [st, setSt] = useState(null);
  useEffect(() => { api.get("/ip-check/status").then((r) => setSt(r.data)).catch(() => {}); }, []);
  if (!st) return null;
  const run = () => api.post("/ip-check/backfill").then(() => toast.success("Checking unchecked clicks from the last 7 days…")).catch((e) => toast.error(e.response?.data?.detail || "Failed"));
  return (
    <div data-testid="ip-check-banner" className={`mb-4 text-xs rounded-lg border px-3 py-2 flex flex-wrap items-center gap-3 ${st.has_key ? "bg-slate-50 border-slate-200 text-slate-600" : "bg-amber-50 border-amber-200 text-amber-800"}`}>
      <span>IP check active (proxycheck.io) · {st.used_today.toLocaleString()} / {st.daily_budget.toLocaleString()} checks used today · {st.unchecked_last_7_days.toLocaleString()} unchecked in last 7 days{!st.has_key && " · running on the free no-key limit — add your proxycheck.io key for 1,000/day"}</span>
      {st.unchecked_last_7_days > 0 && <button data-testid="ip-check-backfill-button" onClick={run} className="font-semibold text-indigo-600">Check them now</button>}
      <span className="ml-auto text-slate-400">Visible only to you</span>
    </div>
  );
};

export default function Clicks() {
  const { user } = useAuth();
  const owner = !!user?.is_owner;
  const [data, setData] = useState({ total: 0, items: [] });
  const [campaigns, setCampaigns] = useState([]);
  const [offers, setOffers] = useState([]);
  const [offerIds, setOfferIds] = useState([]);
  const [rangeSel, setRangeSel] = useState({ range: "today" });
  const [filters, setFilters] = useState({ campaign_id: "", device: "", search: "", ip_type: "" });
  const [page, setPage] = useState(0);
  const [exporting, setExporting] = useState(false);
  const [tick, setTick] = useState(0);
  const [journeyId, setJourneyId] = useState(null);
  const limit = 25;

  const query = useCallback(
    () => `${new URLSearchParams({ ...filters, offer_ids: offerIds.join(",") })}&${rangeParams(rangeSel)}`,
    [filters, offerIds, rangeSel]
  );

  const load = useCallback(() => {
    api.get(`/clicks?${query()}&limit=${limit}&skip=${page * limit}`).then((r) => setData(r.data));
  }, [query, page, tick]);

  useEffect(() => {
    if (!hasPendingLead(data.items)) return;
    const t = setTimeout(() => setTick((x) => x + 1), 15000);
    return () => clearTimeout(t);
  }, [data]);

  useEffect(() => {
    api.get("/campaigns").then((r) => setCampaigns(r.data));
    api.get("/offers").then((r) => setOffers(r.data));
  }, []);
  useEffect(load, [load]);

  const exportCsv = async () => {
    setExporting(true);
    try {
      const res = await api.get(`/clicks/export?${query()}`, { responseType: "blob" });
      const a = document.createElement("a");
      a.href = URL.createObjectURL(res.data);
      const tag = rangeSel.range === "custom" ? `${rangeSel.from}_${rangeSel.to || rangeSel.from}` : rangeSel.range;
      a.download = `omnilink_clicks_${tag}.csv`;
      a.click();
      toast.success(`CSV downloaded — ${data.total.toLocaleString()} clicks`);
    } catch {
      toast.error("CSV download failed");
    }
    setExporting(false);
  };

  const setF = (k, v) => { setFilters((f) => ({ ...f, [k]: v })); setPage(0); };

  return (
    <div data-testid="clicks-page">
      <PageHeader
        title="Clicks Log"
        subtitle={<span data-testid="clicks-total">{`${data.total.toLocaleString()} clicks match these filters — click any row to see the full customer journey`}</span>}
        action={<Button data-testid="export-clicks-button" variant="outline" disabled={exporting} onClick={exportCsv}><Download className="h-4 w-4 mr-1.5" /> {exporting ? "Preparing…" : "Download CSV"}</Button>}
      />
      {owner && <IpCheckBanner />}
      <div className="mb-3 fade-up">
        <DateRangeFilter value={rangeSel} onChange={(v) => { setRangeSel(v); setPage(0); }} />
      </div>
      <div className="flex flex-wrap gap-3 mb-4 fade-up">
        <OfferMultiSelect offers={offers} value={offerIds} onChange={(v) => { setOfferIds(v); setPage(0); }} />
        <Input data-testid="clicks-search-input" placeholder={owner ? "Search click ID, IP, email…" : "Search click ID, email…"} value={filters.search} onChange={(e) => setF("search", e.target.value)} className="max-w-xs bg-white" />
        <Select value={filters.campaign_id || "all"} onValueChange={(v) => setF("campaign_id", v === "all" ? "" : v)}>
          <SelectTrigger data-testid="clicks-campaign-filter" className="w-56 bg-white"><SelectValue placeholder="All campaigns" /></SelectTrigger>
          <SelectContent><SelectItem value="all">All campaigns</SelectItem>{campaigns.map((c) => <SelectItem key={c.id} value={c.id}>{c.name}</SelectItem>)}</SelectContent>
        </Select>
        <Select value={filters.device || "all"} onValueChange={(v) => setF("device", v === "all" ? "" : v)}>
          <SelectTrigger data-testid="clicks-device-filter" className="w-36 bg-white"><SelectValue placeholder="Device" /></SelectTrigger>
          <SelectContent><SelectItem value="all">All devices</SelectItem>{["Desktop", "Mobile", "Tablet"].map((d) => <SelectItem key={d} value={d}>{d}</SelectItem>)}</SelectContent>
        </Select>
        {owner && (
          <Select value={filters.ip_type || "all"} onValueChange={(v) => setF("ip_type", v === "all" ? "" : v)}>
            <SelectTrigger data-testid="clicks-ip-type-filter" className="w-40 bg-white"><SelectValue placeholder="IP type" /></SelectTrigger>
            <SelectContent><SelectItem value="all">All IP types</SelectItem>{IP_TYPE_OPTIONS.map((o) => <SelectItem key={o.v} value={o.v}>{o.l}</SelectItem>)}</SelectContent>
          </Select>
        )}
      </div>
      <Card className="overflow-hidden">
        <div className="overflow-x-auto">
          <table className="w-full">
            <thead><tr><Th>Time</Th><Th>Click ID</Th><Th>Offer</Th><Th>Campaign</Th><Th>Source</Th><Th>Geo</Th><Th>Device</Th>{owner && <Th>IP Check</Th>}<Th>Sub1 / Email</Th><Th>Converted</Th></tr></thead>
            <tbody data-testid="clicks-table-body">
              {data.items.map((c) => (
                <tr key={c.click_id} data-testid={`click-row-${c.click_id}`} className="border-t border-slate-100 hover:bg-indigo-50/40 cursor-pointer" onClick={() => setJourneyId(c.click_id)}>
                  <Td className="whitespace-nowrap text-xs">{fmtDate(c.timestamp)}</Td>
                  <Td><code className="text-[11px] text-indigo-600">{c.click_id}</code></Td>
                  <Td className="text-xs font-medium">{c.offer_name}</Td>
                  <Td className="text-xs">{c.campaign_name}</Td>
                  <Td className="text-xs">{c.traffic_source_name}</Td>
                  <Td className="font-mono text-xs">{c.country}</Td>
                  <Td className="text-xs"><DeviceIcon d={c.device} />{c.device}</Td>
                  {owner && <Td><IpTag stacked check={c.ip_check} ip={c.ip} testId={`click-ip-${c.click_id}`} /></Td>}
                  <Td className="font-mono text-[11px] text-slate-500"><div>{c.email_id || c.sub1 || "—"}</div><LeadTag lead={c.lead_db} testId={`click-lead-${c.click_id}`} /></Td>
                  <Td>{c.converted ? <StatusBadge status="approved" /> : <span className="text-[11px] text-slate-300">—</span>}</Td>
                </tr>
              ))}
              {data.items.length === 0 && (
                <tr><td colSpan={owner ? 10 : 9} data-testid="clicks-empty" className="text-center text-sm text-slate-400 py-10">No clicks match these filters</td></tr>
              )}
            </tbody>
          </table>
        </div>
        <div className="flex items-center justify-between px-4 py-3 border-t border-slate-100">
          <span className="text-xs text-slate-400">Page {page + 1} of {Math.max(1, Math.ceil(data.total / limit))}</span>
          <div className="flex gap-1">
            <Button data-testid="clicks-prev-page" variant="outline" size="sm" className="h-7" disabled={page === 0} onClick={() => setPage((p) => p - 1)}><ChevronLeft className="h-3.5 w-3.5" /></Button>
            <Button data-testid="clicks-next-page" variant="outline" size="sm" className="h-7" disabled={(page + 1) * limit >= data.total} onClick={() => setPage((p) => p + 1)}><ChevronRight className="h-3.5 w-3.5" /></Button>
          </div>
        </div>
      </Card>
      <JourneyDialog clickId={journeyId} onClose={() => setJourneyId(null)} />
    </div>
  );
}
